import AuthenticatedLayout from '@/layouts/AuthenticatedLayout';
import AssessmentPage from '@/pages/Assessment';
import Dashboard from '@/pages/Dashboard';
import Investigations from '@/pages/Investigations';
import InvestigationDetails from '@/pages/InvestigationDetails';
import EmployerReport from '@/pages/EmployerReport';
import Transactions from '@/pages/Transactions';
import EmployeeReports from '@/pages/EmployeeReports';
import AdminEmployeeReports from '@/pages/EmployeeReports/Admin';
import Earnings from '@/pages/Earnings';
import Withdrawals from '@/pages/Withdrawals';
import Settings from '@/pages/Settings';
import Users from '@/pages/Users';
import WaitList from '@/pages/Waitlist';
import Support from '@/pages/Support';
import EmployeeConsent from '@/pages/EmployeeConsent';
import EmployeeSupport from '@/pages/EmployeeSupport';
import InformationRequest from '@/pages/InformationRequest';
import GuarantorRecommendation from '@/pages/GuarantorRecommendation';
import EmployerReference from '@/pages/EmployerReference';
import Login from '@/pages/Auth/Login';
import SignUp from '@/pages/Auth/SignUp';
import VerifyAccount from '@/pages/Auth/VerifyAccount';
import AccountCreated from '@/pages/Auth/AccountCreated';
import HrDashboard from '@/pages/Hr/Dashboard';
import HrEarnings from '@/pages/Hr/Earnings';
import HrLeaderboard from '@/pages/Hr/Leaderboard';
import CustomerSuccess from '@/pages/CustomerSuccess';
import CustomerSuccessDashboard from '@/pages/CustomerSuccess/Dashboard';
import CustomerSuccessDetail from '@/pages/CustomerSuccess/Detail';
import Medical from '@/pages/Medical';
import MedicalDashboard from '@/pages/Medical/Dashboard';
import MedicalDetail from '@/pages/Medical/Detail';
import Notary from '@/pages/Notary';
import NotaryDetail from '@/pages/Notary/Detail';
import Police from '@/pages/Police';
import PoliceDetail from '@/pages/Police/Detail';
import Verify from '@/pages/Verify';
import Blog from '@/pages/Blog';
import BlogDetail from '@/pages/Blog/Detail';
import BlogAdmin from '@/pages/Blog/Admin';
import BlogEditor from '@/pages/Blog/Editor';
import Pricing from '@/pages/Pricing';
import Intelligence from '@/pages/Intelligence';
import Plans from '@/pages/Plans';
import MedicalLabs from '@/pages/MedicalLabs';
import PromoCodes from '@/pages/PromoCodes';
import AdminTransactions from '@/pages/AdminTransactions';
import Callbacks from '@/pages/Callbacks';
import Messages from '@/pages/Messages';
import Training from '@/pages/Training';
import TrainingCourseDetail from '@/pages/Training/CourseDetail';
import TrainingCertificates from '@/pages/Training/Certificates';
import TrainingAdmin from '@/pages/Training/Admin';
import TrainingAdminCourse from '@/pages/Training/AdminCourse';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { UserRole } from '@/types';

// Placeholder components for missing pages
function ErrorPage() {
    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600">Page not found</p>
            </div>
        </div>
    );
}

function PublicLayout() {
    return <Outlet />;
}

// The callback one-pager has its own vanity domain (callme.yourchecka.com). It's
// the same static bundle, so the root route decides by hostname rather than the
// build producing a second artefact. Any `callme.*` host works, which covers
// staging variants; everywhere else keeps the waitlist landing.
// `/intelligence` still serves the page directly on every host.
function LandingSwitch() {
    const host = typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();
    return host === 'callme' || host.startsWith('callme.') ? <Intelligence /> : <WaitList />;
}

// HR partners don't have an investigation pipeline of their own, so /dashboard
// is meaningless for them — bounce them to the HR dashboard. Other roles fall
// through to the normal Dashboard component.
function DashboardSwitch() {
    const currentUser = useAppSelector(selectCurrentUser);
    // Until the role is known (e.g. the moment after login, before the profile
    // resolves), don't fall through to the employer/agent Dashboard — that would
    // briefly mount role-specific data fetches for a role that never uses it.
    if (!currentUser) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#EDEFF3]">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#FF6B35] border-t-transparent" />
            </div>
        );
    }
    if (currentUser?.role === UserRole.HR) {
        return <Navigate to="/hr/dashboard" replace />;
    }
    if (currentUser?.role === UserRole.CUSTOMER_SUCCESS) {
        return <Navigate to="/customer-success" replace />;
    }
    if (currentUser?.role === UserRole.MEDICAL) {
        return <Navigate to="/medical" replace />;
    }
    if (currentUser?.role === UserRole.NOTARY) {
        return <Navigate to="/notary/investigations" replace />;
    }
    if (currentUser?.role === UserRole.POLICE) {
        return <Navigate to="/police/investigations" replace />;
    }
    return <Dashboard />;
}

// Guard HR-only routes. Authenticated non-HR users get bounced to their own
// dashboard rather than seeing a 403 page.
function HrOnly({ children }: { children: React.ReactNode }) {
    const currentUser = useAppSelector(selectCurrentUser);
    if (!currentUser) return null;
    if (currentUser.role !== UserRole.HR) {
        return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
}

// Guard a route to one or more roles; everyone else is bounced to their dashboard.
function RequireRole({ role, children }: { role: UserRole | UserRole[]; children: React.ReactNode }) {
    const currentUser = useAppSelector(selectCurrentUser);
    if (!currentUser) return null;
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(currentUser.role)) {
        return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
}

export const router = createBrowserRouter([
    {
        path: '/',
        element: <PublicLayout />,
        children: [
            {
                index: true,
                element: <LandingSwitch />,
            },
            {
                // Public sales one-pager: positioning, price and callback request
                // in a single view. No auth, no onward navigation.
                path: 'intelligence',
                element: <Intelligence />,
            },
            {
                path: 'login',
                element: (
                    <ErrorBoundary fallback={<Login />}>
                        <Login />
                    </ErrorBoundary>
                ),
            },
            {
                path: 'signup',
                element: <SignUp />,
            },
            {
                path: 'verify-account',
                element: <VerifyAccount />,
            },
            {
                path: 'account-created',
                element: <AccountCreated />,
            },
            {
                path: 'employee-consent/:linkToken',
                element: <EmployeeConsent />,
            },
            {
                path: 'background-check/information-request/:token',
                element: <InformationRequest />,
            },
            {
                // This exact path is baked into the outgoing guarantor emails/SMS
                // (built from the API's FRONTEND_URL). Do not rename it — every link
                // already sent would break.
                path: 'guarantor-recommendation/:token',
                element: <GuarantorRecommendation />,
            },
            {
                // Baked into outgoing employer-reference emails/SMS — do not rename.
                path: 'employer-reference/:token',
                element: <EmployerReference />,
            },
            {
                // Public QR trust card. The agent's QR encodes {origin}/verify/{userId}.
                path: 'verify/:id',
                element: <Verify />,
            },
            {
                path: 'blog',
                element: <Blog />,
            },
            {
                path: 'blog/:slug',
                element: <BlogDetail />,
            },
            {
                path: 'support/:supportCode',
                element: <EmployeeSupport />,
            },
            {
                path: 'support',
                element: <EmployeeSupport />,
            },
            {
                path: 'investigations/:id/report',
                element: <EmployerReport />,
            },
            {
                path: 'about',
                element: (
                    <div className="p-8">
                        <h1 className="text-3xl font-bold">About Page</h1>
                    </div>
                ),
            },
            {
                element: <AuthenticatedLayout />,
                children: [
                    {
                        path: 'dashboard',
                        element: <DashboardSwitch />,
                    },
                    {
                        path: 'hr/dashboard',
                        element: (
                            <HrOnly>
                                <HrDashboard />
                            </HrOnly>
                        ),
                    },
                    {
                        path: 'hr/earnings',
                        element: (
                            <HrOnly>
                                <HrEarnings />
                            </HrOnly>
                        ),
                    },
                    {
                        path: 'hr/leaderboard',
                        element: (
                            <HrOnly>
                                <HrLeaderboard />
                            </HrOnly>
                        ),
                    },
                    // Customer Success — dashboard, pipeline list, detail
                    {
                        path: 'customer-success',
                        element: (
                            <RequireRole role={UserRole.CUSTOMER_SUCCESS}>
                                <CustomerSuccessDashboard />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'customer-success/investigations',
                        element: (
                            <RequireRole role={UserRole.CUSTOMER_SUCCESS}>
                                <CustomerSuccess />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'customer-success/:id',
                        element: (
                            <RequireRole role={UserRole.CUSTOMER_SUCCESS}>
                                <CustomerSuccessDetail />
                            </RequireRole>
                        ),
                    },
                    // Medical — dashboard, assigned list, test panel + report entry
                    {
                        path: 'medical',
                        element: (
                            <RequireRole role={UserRole.MEDICAL}>
                                <MedicalDashboard />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'medical/investigations',
                        element: (
                            <RequireRole role={UserRole.MEDICAL}>
                                <Medical />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'medical/:id',
                        element: (
                            <RequireRole role={UserRole.MEDICAL}>
                                <MedicalDetail />
                            </RequireRole>
                        ),
                    },
                    // Notary — assigned list + affidavit review/upload (Super Admin also allowed)
                    {
                        path: 'notary/investigations',
                        element: (
                            <RequireRole role={[UserRole.NOTARY, UserRole.SUPER_ADMIN]}>
                                <Notary />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'notary/:id',
                        element: (
                            <RequireRole role={[UserRole.NOTARY, UserRole.SUPER_ADMIN]}>
                                <NotaryDetail />
                            </RequireRole>
                        ),
                    },
                    // Police — assigned list + profile download / certificate upload
                    // (Super Admin also allowed).
                    {
                        path: 'police/investigations',
                        element: (
                            <RequireRole role={[UserRole.POLICE, UserRole.SUPER_ADMIN]}>
                                <Police />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'police/:id',
                        element: (
                            <RequireRole role={[UserRole.POLICE, UserRole.SUPER_ADMIN]}>
                                <PoliceDetail />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'investigations',
                        element: <Investigations />,
                    },
                    {
                        path: 'investigations/:id',
                        element: <InvestigationDetails />,
                    },
                    {
                        path: 'transactions',
                        element: <Transactions />,
                    },
                    {
                        // Employer files/reviews derogatory reports. Visibility is driven
                        // by the sidebar (employer-only nav entry).
                        path: 'employee-reports',
                        element: <EmployeeReports />,
                    },
                    {
                        // Admin / CS review queue for all filed reports.
                        path: 'admin/employee-reports',
                        element: (
                            <RequireRole role={[UserRole.SUPER_ADMIN, UserRole.CUSTOMER_SUCCESS]}>
                                <AdminEmployeeReports />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'earnings',
                        element: <Earnings />,
                    },
                    {
                        path: 'withdrawals',
                        element: <Withdrawals />,
                    },
                    {
                        path: 'users',
                        element: <Users />,
                    },
                    {
                        path: 'pricing',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <Pricing />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'plans',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <Plans />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'medical-labs',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <MedicalLabs />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'promo-codes',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <PromoCodes />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'admin/transactions',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <AdminTransactions />
                            </RequireRole>
                        ),
                    },
                    {
                        // Lead queue. The API allows super_admin and customer_success;
                        // the guard mirrors that so the roles can't drift apart.
                        path: 'callbacks',
                        element: (
                            <RequireRole role={[UserRole.SUPER_ADMIN, UserRole.CUSTOMER_SUCCESS]}>
                                <Callbacks />
                            </RequireRole>
                        ),
                    },
                    // Blog CMS — Super Admin authoring. `new` is registered before
                    // `:id` so the create route isn't swallowed by the edit route.
                    {
                        path: 'admin/blog',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <BlogAdmin />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'admin/blog/new',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <BlogEditor />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'admin/blog/:id',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <BlogEditor />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'assessment',
                        element: <AssessmentPage />,
                    },
                    // Training — agent flow
                    {
                        path: 'training',
                        element: (
                            <RequireRole role={UserRole.AGENT}>
                                <Training />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'training/certificates',
                        element: (
                            <RequireRole role={UserRole.AGENT}>
                                <TrainingCertificates />
                            </RequireRole>
                        ),
                    },
                    // Training — admin authoring (registered before :courseId)
                    {
                        path: 'training/admin',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <TrainingAdmin />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'training/admin/:courseId',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <TrainingAdminCourse />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'training/:courseId',
                        element: (
                            <RequireRole role={UserRole.AGENT}>
                                <TrainingCourseDetail />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'admin/messages',
                        element: (
                            <RequireRole role={UserRole.SUPER_ADMIN}>
                                <Messages />
                            </RequireRole>
                        ),
                    },
                    {
                        path: 'results',
                        element: (
                            <div className="p-8">
                                <h1 className="text-3xl font-bold">Results Page</h1>
                                <p className="text-gray-600 mt-4">Results page coming soon...</p>
                            </div>
                        ),
                    },
                    {
                        path: 'learning-path',
                        element: (
                            <div className="p-8">
                                <h1 className="text-3xl font-bold">Learning Path</h1>
                                <p className="text-gray-600 mt-4">Learning path page coming soon...</p>
                            </div>
                        ),
                    },
                    {
                        path: 'settings',
                        element: <Settings />,
                    },
                    {
                        // Authenticated admin support inbox. Lives at /admin/support to
                        // avoid colliding with the public /support (consent-code) page
                        // above — both registered with path 'support' meant React Router
                        // always matched the public one first.
                        path: 'admin/support',
                        element: <Support />,
                    },
                ],
            },
            {
                path: '*',
                element: <ErrorPage />,
            },
        ],
    },
]);

export default router;
